import { match } from '@frontend/duck-tape';
import { useEffect, useMemo, useState } from '@frontend/react';
import { CardContainer, Clickable, Icon, Text, XStack, YStack } from '@frontend/web-react';
import { useListAddressesQuery, useRetrieveClientQuery } from '@frontend/web/hooks';

import { PROFILE_WIZARD_STEP_ORDER } from './ProfileWizardCard.constants';
import type { ProfileWizardState, ProfileWizardStep } from './ProfileWizardCard.types';
import { PROFILE_WIZARD_STEP } from './ProfileWizardCard.types';
import { ProfileWizardContent } from './ProfileWizardContent';

const CTA_ICON_SIZE = 48;

const computeCompletedSteps = (
  phoneNumber: MaybeUndefined<string>,
  birthday: MaybeUndefined<string>,
  hasAddress: boolean,
): ReadonlyArray<ProfileWizardStep> =>
  PROFILE_WIZARD_STEP_ORDER.filter((step) =>
    match(step)
      .with(PROFILE_WIZARD_STEP.address, () => hasAddress)
      .with(PROFILE_WIZARD_STEP.birthday, () => Boolean(birthday))
      .with(PROFILE_WIZARD_STEP.phoneNumber, () => Boolean(phoneNumber))
      .exhaustive(),
  );

const computeInitialStep = (completedSteps: ReadonlyArray<ProfileWizardStep>): ProfileWizardStep =>
  PROFILE_WIZARD_STEP_ORDER.find((s) => !completedSteps.includes(s)) ?? PROFILE_WIZARD_STEP.address;

export const ProfileWizardCard = () => {
  const { data: client, isLoading: isClientLoading } = useRetrieveClientQuery();
  const { data: addresses, isLoading: isAddressesLoading } = useListAddressesQuery();
  const isLoading = isClientLoading || isAddressesLoading;

  const completedSteps = useMemo(
    () => computeCompletedSteps(client?.phoneNumber, client?.birthday, Boolean(addresses?.length)),
    [client?.birthday, client?.phoneNumber, addresses?.length],
  );
  const [isWizardStarted, setIsWizardStarted] = useState(false);

  useEffect(() => {
    if (completedSteps.length > 0) {
      setIsWizardStarted(true);
    }
  }, [completedSteps]);

  const initialStep = useMemo(() => computeInitialStep(completedSteps), [completedSteps]);

  const initialState: ProfileWizardState = useMemo(
    () => ({
      completedSteps,
      currentStep: initialStep,
    }),
    [completedSteps, initialStep],
  );

  if (isLoading) return null;

  const isAllComplete = completedSteps.length === PROFILE_WIZARD_STEP_ORDER.length;
  if (isAllComplete) return null;

  if (!isWizardStarted) {
    return (
      <CardContainer className="p-lg border-borderTertiary">
        <YStack gapY="md">
          <div
            className="flex items-center justify-center rounded-lg bg-surfaceAccent"
            style={{ height: CTA_ICON_SIZE, width: CTA_ICON_SIZE }}
          >
            <Icon color="primary" name="IconUser" size="md" />
          </div>
          <YStack gapY="xs">
            <Text type="h4">Personalize Duckbill for you</Text>
            <Text color="secondary" type="p-sm">
              Add a few details to your profile so we can handle tasks faster and smarter.
            </Text>
          </YStack>
          <Clickable onClick={() => setIsWizardStarted(true)}>
            <XStack alignItems="center" gapX="xs">
              <Text className="font-medium" type="p-sm">
                {"Let's go"}
              </Text>
              <Icon color="primary" name="IconChevronRight" size="xs" />
            </XStack>
          </Clickable>
        </YStack>
      </CardContainer>
    );
  }

  return client ? (
    <ProfileWizardContent client={client} existingAddress={addresses?.[0]} initialState={initialState} />
  ) : null;
};
